import { Target, Eye, Medal, Certificate, Truck, Heartbeat } from "@phosphor-icons/react";
import { IMG } from "@/lib/products";
import { yearsSince } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { PageHero, SectionHeading } from "@/components/PageBits";
import { SmartImage } from "@/components/SmartImage";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { FounderSection } from "@/components/FounderSection";

const VALUE_ICONS = [Medal, Heartbeat, Certificate, Truck];

export default function About() {
  const { t } = useLang();
  const years = yearsSince();
  const values = t("about.values");

  const stats = [
    { v: years, s: "+", l: t("stats.years") },
    { v: 50000, s: "+", l: t("stats.customersShort") },
    { v: t("stats.isoValue"), s: "", l: t("stats.certified") },
    { v: t("stats.panIndiaValue"), s: "", l: t("stats.supplyNetwork") },
  ];

  return (
    <div>
      <PageHero eyebrow={t("about.eyebrow")} title={t("about.title")} subtitle={t("about.subtitle")} image={IMG.factory} />

      {/* Overview */}
      <section className="container-x py-16 md:py-24" data-testid="about-overview">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="overline mb-4">{t("about.overviewEyebrow")}</p>
            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-forest md:text-4xl">
              {t("about.overviewTitle")}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-ink/75">{t("about.overviewP1")}</p>
            <p className="mt-4 leading-relaxed text-subtle">{t("about.overviewP2")}</p>
          </Reveal>
          <Reveal delay={0.1} className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-champagne shadow-lg shadow-forest/10 ring-1 ring-hairline">
            <SmartImage src={IMG.heroAgriculture} alt="RK Jain heritage" className="h-full w-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-champagne py-16">
        <div className="container-x grid grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map((x, i) => (
            <Reveal key={i} className="border-l-2 border-gold pl-4">
              <div className="font-heading text-3xl font-extrabold text-forest md:text-4xl">
                <Counter value={x.v} suffix={x.s} />
              </div>
              <p className="mt-2 text-sm font-medium text-subtle">{x.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Founder */}
      <FounderSection />

      {/* Mission / Vision */}
      <section className="container-x py-16 md:py-24">
        <div className="grid gap-6 md:grid-cols-2">
          <Reveal className="rounded-2xl border border-hairline bg-surface p-8 shadow-sm md:p-12">
            <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-champagne text-forest">
              <Target size={36} weight="thin" />
            </span>
            <h3 className="mt-6 font-heading text-2xl font-bold text-forest">{t("about.missionTitle")}</h3>
            <p className="mt-4 leading-relaxed text-ink/75">{t("about.missionText")}</p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-2xl border border-hairline bg-surface p-8 shadow-sm md:p-12">
            <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-champagne text-forest">
              <Eye size={36} weight="thin" />
            </span>
            <h3 className="mt-6 font-heading text-2xl font-bold text-forest">{t("about.visionTitle")}</h3>
            <p className="mt-4 leading-relaxed text-ink/75">{t("about.visionText")}</p>
          </Reveal>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-champagne py-16 md:py-24">
        <div className="container-x">
          <SectionHeading eyebrow={t("about.valuesEyebrow")} title={t("about.valuesTitle")} />
          <StaggerGroup className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <StaggerItem key={i} className="rounded-2xl border border-hairline bg-surface p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                  <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-champagne text-forest">
                    <Icon size={30} weight="thin" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-forest">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-subtle">{v.text}</p>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
}
